import { trpc } from "@/lib/trpc";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import SeoTags from "@/components/SeoTags";
import { getCatalogFilter, getCatalogFilterPath } from "@/lib/routes";
import NotFound from "@/pages/NotFound";
import {
  getModelFamilyGuide,
  getModelFamilyGuides,
  getModelFamilyGuidePath,
  matchProductsToModelFamily,
} from "@shared/modelFamilyGuides";
import { buildModelFamilySeo } from "@shared/seo";
import { useMemo } from "react";
import { Link, useParams } from "wouter";

export default function ModelFamilyGuide() {
  const params = useParams<{ slug?: string }>();
  const guide = useMemo(() => getModelFamilyGuide(params.slug), [params.slug]);

  const { data: allProducts, isLoading } = trpc.products.all.useQuery(
    { sortBy: "sales", sortDir: "desc" },
    {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const matchedProducts = useMemo(
    () => (guide ? matchProductsToModelFamily(allProducts || [], guide) : []),
    [allProducts, guide]
  );
  const relatedGuides = useMemo(
    () =>
      getModelFamilyGuides()
        .filter(entry => entry.slug !== guide?.slug)
        .slice(0, 4),
    [guide?.slug]
  );

  if (!guide) {
    return <NotFound />;
  }

  const seoMetadata =
    typeof window === "undefined"
      ? buildModelFamilySeo({ guide, products: matchedProducts })
      : buildModelFamilySeo({
          origin: window.location.origin,
          guide,
          products: matchedProducts,
        });

  return (
    <div
      className="min-h-screen flex flex-col bg-background"
      style={{ backgroundColor: "var(--background)" }}
    >
      <SeoTags metadata={seoMetadata} />
      <Navbar />

      <main className="container flex-1 py-6">
        <section className="overflow-hidden rounded-[1.3rem] border border-border bg-card px-5 py-5 md:px-6 md:py-6">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-primary">
            Model Family Guide
          </p>
          <h1 className="mt-3 text-[1.45rem] font-semibold tracking-[-0.04em] text-foreground md:text-[1.9rem]">
            {guide.headline}
          </h1>
          <p className="mt-3 max-w-[52rem] text-sm leading-7 text-muted-foreground md:text-[0.98rem]">
            {guide.intro}
          </p>
          <p className="mt-3 max-w-[52rem] text-sm leading-7 text-muted-foreground md:text-[0.98rem]">
            {guide.details}
          </p>
        </section>

        <section className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
          <div className="rounded-[1.35rem] border border-border bg-card p-5 md:p-6">
            <h2 className="text-[1.2rem] font-semibold tracking-[-0.03em] text-foreground md:text-[1.45rem]">
              {guide.shortLabel} products on Techfare
            </h2>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              These are the strongest matching products in the current catalog for
              this family page.
            </p>

            {isLoading ? (
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-xl border border-border bg-background animate-pulse"
                  >
                    <div className="aspect-square bg-secondary/50" />
                    <div className="space-y-3 p-4">
                      <div className="h-4 w-3/4 rounded bg-secondary" />
                      <div className="h-6 w-1/2 rounded bg-secondary" />
                    </div>
                  </div>
                ))}
              </div>
            ) : matchedProducts.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {matchedProducts.slice(0, 12).map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    title={product.marketModel}
                    interactionMode="static"
                    compactMobile
                    showBidAsk={false}
                    analyticsSource={`guide-${guide.slug}`}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-[1rem] border border-border bg-background px-4 py-5">
                <p className="text-sm leading-7 text-muted-foreground">
                  Techfare does not have matching products for this family in the
                  current catalog yet, but the guide is live so search intent and
                  future catalog coverage can converge on one canonical page.
                </p>
              </div>
            )}

            <div className="mt-7 grid gap-3 md:grid-cols-3">
              {guide.valueDrivers.map(driver => (
                <div
                  key={driver.title}
                  className="rounded-[1rem] border border-border bg-background p-4"
                >
                  <h3 className="text-sm font-semibold text-foreground">
                    {driver.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {driver.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            <section className="rounded-[1.35rem] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground">
                Search targets
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {guide.queryTargets.map(query => (
                  <span
                    key={query}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground"
                  >
                    {query}
                  </span>
                ))}
              </div>
            </section>

            <section className="rounded-[1.35rem] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground">
                Related markets
              </h2>
              <div className="mt-4 space-y-2.5">
                {guide.relatedFilterSlugs.map(slug => (
                  <Link
                    key={slug}
                    href={getCatalogFilterPath(slug)}
                    className="block rounded-[0.95rem] border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/35 hover:bg-primary/5"
                  >
                    {getCatalogFilter(slug)?.label || slug.replace(/-/g, " ")}
                  </Link>
                ))}
              </div>
            </section>

            <section className="rounded-[1.35rem] border border-border bg-card p-5">
              <h2 className="text-base font-semibold text-foreground">
                Related guides
              </h2>
              <div className="mt-4 space-y-2.5">
                {relatedGuides.map(entry => (
                  <Link
                    key={entry.slug}
                    href={getModelFamilyGuidePath(entry.slug)}
                    className="block rounded-[0.95rem] border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:border-primary/35 hover:bg-primary/5"
                  >
                    {entry.shortLabel}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </section>

        <section className="mt-5 rounded-[1.35rem] border border-border bg-card p-5 md:p-6">
          <h2 className="text-[1.2rem] font-semibold tracking-[-0.03em] text-foreground md:text-[1.45rem]">
            {guide.shortLabel} FAQ
          </h2>
          <div className="mt-4 space-y-3">
            {guide.faq.map(item => (
              <details
                key={item.question}
                className="rounded-[1rem] border border-border bg-background px-4 py-3"
              >
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground">
                  {item.question}
                </summary>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
